class PreloaderController {
    /**
     * @param {Object} options
     * @param {string} options.videoUrl - URL for main video file.
     * @param {HTMLElement} options.preloaderElement - Root preloader element.
     * @param {HTMLElement} options.progressTextElement - Element showing numeric progress.
     * @param {HTMLElement} options.videoContainerElement - Container for preloader video.
     * @param {HTMLVideoElement} options.videoElement - Preloader video element.
     * @param {Function} [options.onVideoReady] - Callback when video blob url is ready.
     * @param {Function} options.onComplete - Callback when preloader is fully done.
     */
    constructor({
        videoUrl,
        preloaderElement,
        progressTextElement,
        videoContainerElement,
        videoElement,
        onVideoReady,
        onComplete
    }) {
        this.videoUrl              = videoUrl;
        this.preloaderElement      = preloaderElement;
        this.progressTextElement   = progressTextElement;
        this.videoContainerElement = videoContainerElement;
        this.videoElement          = videoElement;
        this.onVideoReady          = onVideoReady;
        this.onComplete            = onComplete;

        this.progress       = 0;
        this.isCompleted    = false;
        this.minDisplayTime = 1200;
        this.startTime      = null;
        this.hasVideoReady  = false;
    }

    static isDebugEnabled() {
        try {
            return window.localStorage && window.localStorage.getItem('experienceDebug') === '1';
        } catch (error) {
            return false;
        }
    }

    debugLog(eventName, payload = {}) {
        if (!PreloaderController.isDebugEnabled()) {
            return;
        }

        const safePayload = {
            videoUrl : this.videoUrl,
            ...payload
        };

        // eslint-disable-next-line no-console
        console.info('[PreloaderController]', eventName, safePayload);
    }

    notifyVideoReady(videoSrc) {
        if (this.hasVideoReady) {
            return;
        }

        this.hasVideoReady = true;

        if (typeof this.onVideoReady === 'function') {
            this.onVideoReady(videoSrc);
        }

        this.debugLog('video_ready', {
            videoSrc
        });
    }

    /**
     * Initialize loading and progress.
     */
    init() {
        try {
            this.startTime = performance.now();
            this.updateProgress(0);

            this.debugLog('init', {
                videoUrl : this.videoUrl
            });

            if (this.videoContainerElement) {
                this.videoContainerElement.classList.add('experience-hero__video-shell--visible');
                this.videoContainerElement.style.setProperty('--tilt-x', '0');
                this.videoContainerElement.style.setProperty('--tilt-y', '0');
            }

            this.loadVideoWithProgress();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Preloader initialization failed', error);
            this.debugLog('init_error', {
                message : error && error.message ? error.message : String(error)
            });
            this.finishImmediately();
        }
    }

    /**
     * Update visual progress and CSS variable.
     * @param {number} value
     */
    updateProgress(value) {
        this.progress = Math.max(0, Math.min(100, value));
        document.body.style.setProperty('--progress', String(this.progress));

        if (this.progressTextElement) {
            this.progressTextElement.textContent = `${this.progress}%`;
        }

        if (this.preloaderElement) {
            if (this.progress >= 50) {
                const cappedProgress                = Math.min(this.progress, 100);
                const localProgress                 = (cappedProgress - 50) / 50;
                const opacity                       = Math.max(0, 1 - localProgress);
                this.preloaderElement.style.opacity = String(opacity);
            } else {
                this.preloaderElement.style.opacity = '';
            }
        }
    }

    /**
     * Load video via XHR/fetch to track progress.
     */
    loadVideoWithProgress() {
        const LOAD_TIMEOUT_MS = 45000;
        const xhr = new XMLHttpRequest();
        xhr.open('GET', this.videoUrl, true);
        xhr.responseType = 'arraybuffer';
        xhr.timeout      = LOAD_TIMEOUT_MS;

        this.debugLog('xhr_start', {
            timeoutMs : LOAD_TIMEOUT_MS
        });

        xhr.onprogress = (event) => {
            if (!event.lengthComputable) {
                return;
            }

            const rawProgress = (event.loaded / event.total) * 100;
            const clamped     = Math.floor(rawProgress);
            this.updateProgress(clamped);
        };

        xhr.onerror = () => {
            this.notifyVideoReady(this.videoUrl);
            this.debugLog('xhr_error');
            this.finishImmediately();
        };

        xhr.ontimeout = () => {
            this.notifyVideoReady(this.videoUrl);
            this.debugLog('xhr_timeout');
            this.finishImmediately();
        };

        xhr.onabort = () => {
            this.notifyVideoReady(this.videoUrl);
            this.debugLog('xhr_abort');
            this.finishImmediately();
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                const blob    = new Blob([xhr.response], { type : 'video/mp4' });
                const blobUrl = URL.createObjectURL(blob);

                this.notifyVideoReady(blobUrl);

                this.debugLog('xhr_success', {
                    status : xhr.status
                });

                if (this.videoElement) {
                    this.videoElement.src         = blobUrl;
                    this.videoElement.muted       = true;
                    this.videoElement.playsInline = true;
                    this.videoElement.currentTime = 0;
                }

                this.updateProgress(100);
                this.handleComplete();
            } else {
                this.notifyVideoReady(this.videoUrl);
                this.debugLog('xhr_bad_status', {
                    status : xhr.status
                });
                this.finishImmediately();
            }
        };

        xhr.send();
    }

    /**
     * Handle completion with minimum display time.
     */
    handleComplete() {
        if (this.isCompleted) {
            return;
        }

        const now       = performance.now();
        const elapsed   = this.startTime ? now - this.startTime : 0;
        const remaining = Math.max(0, this.minDisplayTime - elapsed);

        window.setTimeout(() => {
            this.isCompleted = true;
            this.hidePreloader();
        }, remaining);
    }

    /**
     * Fallback completion if loading fails.
     */
    finishImmediately() {
        this.updateProgress(100);
        this.handleComplete();
    }

    /**
     * Apply fade-out and then call onComplete.
     */
    hidePreloader() {
        if (!this.preloaderElement) {
            if (typeof this.onComplete === 'function') {
                this.onComplete();
            }
            return;
        }

        if (typeof this.onComplete === 'function') {
            this.onComplete();
        }

        this.preloaderElement.classList.add('experience-preloader--done');

        window.setTimeout(() => {
            this.preloaderElement.classList.add('experience-preloader--hidden');
        }, 700);
    }

    /**
     * Mouse move parallax effect.
     */
    bindMouseParallax() {
        if (!this.preloaderElement || !this.videoContainerElement) {
            return;
        }

        const PARALLAX_MULTIPLIER = 1.6;

        const handleMove = (event) => {
            const {
                clientX,
                clientY
            } = event;
            const {
                innerWidth,
                innerHeight
            } = window;

            const normX = ((clientX / innerWidth) * 2 - 1) * PARALLAX_MULTIPLIER;
            const normY = ((clientY / innerHeight) * 2 - 1) * PARALLAX_MULTIPLIER;

            this.videoContainerElement.style.setProperty('--tilt-x', String(normX));
            this.videoContainerElement.style.setProperty('--tilt-y', String(normY));
        };

        this.preloaderElement.addEventListener('mousemove', handleMove);
    }
}

export default PreloaderController;
